// Added by Sejune Oh on 2019-03-19
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  ReactClickEvent,
  SORT_OPTION,
  ReactTablePagination,
  ReactInputChangeEvent,
  TABLE_HEADER_ALIGN_OPTION,
  ReactInputCheckedEvent,
  ROWS_PER_PAGE,
  ReactEmptyEvent,
} from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Components
import Table from './Table'
import TableHeader from './TableHeader'
import TableBody from './TableBody'
import TableFooter from './TableFooter'
import { UserPrefStore } from '@/stores/user/UserPref'

// Style
import { TABLE_ROW_WIDTH, TABLE_ROW_HEIGHT } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
// [1] Header
export interface TableHeaderPartialProps {
  headerProps: Array<TableHeaderProps>
  orderBy?: string
  order?: SORT_OPTION
  onSortClick?: TableSortBtnClickEvent
  customColSpan?: Array<number>
}

export interface SelectableTablePartialProps {
  onSelectAllChange?: ReactInputCheckedEvent
  onSelectAllClick?: ReactEmptyEvent
  selectedNum: number
  rowsNum: number
  isAllSelected: boolean
  defaultRowWidth: number
  customColSpan?: Array<number>
}

// [2] Body
export interface TableBodyPartialProps {
  rows: Array<any>
  emptyRowsCount: number
  colSpan: number
  onTableRowClick: ReactClickEvent
  headerProps: Array<TableHeaderProps>
  onSelectClick?: ReactClickEvent
  selectedIndexArr: Set<number>
  defaultRowHeight: number
  isRowSelectable?: boolean
  customColSpan?: Array<number>
}

// [3] Footer

// [4] ETC
export interface TableHeaderProps {
  key: string
  disp_key?: string
  align?: TABLE_HEADER_ALIGN_OPTION
  disablePadding?: boolean
  label?: string
  isSortable?: boolean
  id?: string
  type?: 'string' | 'number' | 'date' | 'boolean' | 'jsx'
}

export interface TableFunctionalComponentProps {
  onSearch: (key: string) => void
  onAddBtnClick: ReactEmptyEvent
  defaultSearchKeyword?: string
  tableComponent: JSX.Element
}

export interface TableComponentInitProps {
  isSortable?: boolean
  isSelectable?: boolean
  isRowSelectable?: boolean
  isPaginationable?: boolean
  headerProps: Array<TableHeaderProps>
  customSetter?: CustomSetterProps
  basePath?: string
  info?: string
}

export interface TableUrlQueryProps {
  page_index?: string
  page_size?: string
  order_by?: string
  order?: SORT_OPTION
  filter?: string
  pageIndex?: number
  pageSize?: number
  orderBy?: string
}

export interface CustomSetterProps {
  name: string
  args: Array<any>
}

export interface TableBaseCProps extends TableUrlQueryProps, TableComponentInitProps {
  updateSearchKeyword?: (key: string) => void
}

export interface TableRowItemProps {
  nonenumerable?: boolean
}

export interface TableOptionProps {
  onRowClick?: ReactClickEvent
  colSpan?: Array<number>
}

interface TableCProps extends TableBaseCProps, TableOptionProps {
  elements: Array<any>
  totalCount: number
  queryParam?: TableUrlQueryProps
  filterKey?: string
  userPref?: UserPrefStore
}

// Type
export type TableSortBtnClickEvent = (property: string) => ReactClickEvent

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function getTableHeaderOption({
  key,
  disp_key,
  align = 'left',
  disablePadding = false,
  label = '',
  isSortable = false,
  type = 'string',
}: TableHeaderProps): TableHeaderProps {
  return {
    label: !!label ? label : key,
    key,
    disp_key: disp_key || key,
    align,
    disablePadding,
    isSortable,
    type,
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

@inject('userPref')
@observer
class TableContainer extends Component<TableCProps> {
  elements: Array<any> = []
  totalCount: number = this.props.totalCount
  selectedIndexArr: Set<number> = new Set()
  isAllSelected: boolean = false
  nonenumerableElemCount: number = 0
  getQueryParams: TableUrlQueryProps = {
    pageIndex: 0,
    pageSize: ROWS_PER_PAGE[0],
    orderBy: undefined,
    order: 'desc',
    filter: undefined,
    ...this.props.queryParam,
  }

  constructor(props: TableCProps) {
    super(props)
    makeObservable(this, {
      elements: observable,
      totalCount: observable,
      selectedIndexArr: observable,
      isAllSelected: observable,
      nonenumerableElemCount: observable,
      getQueryParams: observable,
      componentDidMount: action,
      _getData: action,
      handleSort: action,
      handleSelectAll: action,
      handleSelectAllToggle: action,
      _selectAllTask: action,
      clearAllSelected: action,
      handleSelect: action,
      handleChangePage: action,
      handleChangePageSize: action,
      handleSearch: action,
      unselectItemById: action,
      selectItemById: action,
      userPref: computed,
      selectedNum: computed,
      selectedIndex: computed,
      selectedIndexAsArray: computed,
      rowsNum: computed,
      colSpanCount: computed,
      emptyRowsCount: computed,
      selectedList: computed,
      customColSpanCount: computed,
      pageIndex: computed,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  componentDidMount() {
    this.getQueryParams.pageSize = this.userPref.rowsPerPage || ROWS_PER_PAGE[0]
    this._getData()
  }

  render() {
    const customColSpan = this.props.colSpan || undefined

    return (
      <Table
        tableHeadComponent={
          <TableHeader
            onSort={this.handleSort}
            onSelectAll={this.handleSelectAll}
            onSelectAllToggle={this.handleSelectAllToggle}
            isSortable={this.props.isSortable}
            isSelectable={this.props.isSelectable}
            headerProps={this.props.headerProps}
            isAllSelected={this.isAllSelected}
            selectedNum={this.selectedNum}
            rowsNum={this.rowsNum}
            order={this.getQueryParams.order}
            orderBy={this.getQueryParams.orderBy}
            defaultRowWidth={TABLE_ROW_WIDTH}
            customColSpan={customColSpan}
          />
        }
        tableBodyComponent={
          <TableBody
            onSelect={this.handleSelect}
            isSelectable={this.props.isSelectable}
            isRowSelectable={this.props.isRowSelectable}
            rows={this.elements}
            emptyRowsCount={this.emptyRowsCount}
            colSpan={this.colSpanCount}
            onTableRowClick={this.handleTableRowClick}
            headerProps={this.props.headerProps}
            selectedIndexArr={this.selectedIndexArr}
            defaultRowHeight={TABLE_ROW_HEIGHT}
            customColSpan={customColSpan}
          />
        }
        tableFooterComponent={
          <TableFooter
            isPaginatable={this.props.isPaginationable}
            count={this.totalCount}
            page={this.getQueryParams.pageIndex as number}
            rowsPerPage={this.getQueryParams.pageSize as number}
            rowsPerPageOptions={ROWS_PER_PAGE}
            colSpan={this.customColSpanCount}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangePageSize}
            info={this.props.info}
          />
        }
      />
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get userPref() {
    return this.props.userPref!
  }

  get selectedNum() {
    return this.selectedIndexArr.size
  }

  get selectedIndex() {
    return this.selectedIndexArr
  }

  get selectedIndexAsArray() {
    return Array.from(this.selectedIndexArr)
  }

  get rowsNum() {
    return this.props.elements.length
  }

  get colSpanCount() {
    return this.props.headerProps.length * 2 + 1
  }

  get emptyRowsCount() {
    if (this.props.isPaginationable) return (this.getQueryParams.pageSize as number) - this.props.elements.length

    return 0
  }

  get selectedList() {
    if (!this.props.isSelectable) return []

    if (this.isAllSelected) {
      return this.props.elements.filter((item: any) => !item.nonenumerable)
    }

    return this.props.elements.filter((item: any, index: number) => this.selectedIndexArr.has(index))
  }

  get customColSpanCount() {
    if (!this.props.colSpan) return this.colSpanCount

    let colSpanCount = 0
    this.props.colSpan.forEach((num) => {
      colSpanCount += num
    })

    return colSpanCount + 1
  }

  get pageIndex() {
    return this.getQueryParams.pageIndex
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _getData = async () => {
    this.nonenumerableElemCount = 0

    this.props.elements.map(({ nonenumerable }: any) => {
      if (nonenumerable) this.nonenumerableElemCount++
      return nonenumerable
    })

    if (this.nonenumerableElemCount === this.props.elements.length) {
      this.isAllSelected = false
    } else {
      this.isAllSelected = this.selectedIndexArr.size === this.props.elements.length - this.nonenumerableElemCount
    }

    const { pageIndex, pageSize, orderBy, order, filter } = this.getQueryParams
    this.elements = await this._filterElements(filter)
      .then((rtnData) => {
        this.totalCount = rtnData.length
        return this._sortElements(rtnData, order, orderBy)
      })
      .then((rtnData) => {
        if (this.props.isPaginationable) return this._splitElements(rtnData, pageSize!, pageIndex!)
        else return rtnData
      })
  }

  handleSort = (prop: string) => {
    this.getQueryParams.order =
      this.getQueryParams.orderBy === prop && this.getQueryParams.order === 'desc' ? 'asc' : 'desc'
    this.getQueryParams.orderBy = prop
    this._getData()
  }

  handleSelectAll = (active: boolean) => {
    this.isAllSelected = active
    this._selectAllTask(active)
  }

  handleSelectAllToggle = () => {
    this.isAllSelected = !this.isAllSelected
    this._selectAllTask(this.isAllSelected)
  }

  _selectAllTask = (active: boolean) => {
    if (active) {
      this.props.elements.map(({ nonenumerable }: any, index: number) => {
        if (!nonenumerable) this.selectedIndexArr.add(index)
        return nonenumerable
      })
    } else {
      this.selectedIndexArr.clear()
    }
  }

  clearAllSelected = () => {
    this.isAllSelected = false
    this.selectedIndex.clear()
  }

  handleSelect = (index: number) => {
    if (this.selectedIndexArr.has(index)) {
      this.selectedIndexArr.delete(index)
      this.isAllSelected = false
      return
    }

    this.selectedIndexArr.add(index)
    this.isAllSelected = this.selectedIndexArr.size === this.props.elements.length - this.nonenumerableElemCount
  }

  handleChangePage: ReactTablePagination = (pageEvent, page) => {
    this.getQueryParams.pageIndex = page
    this._getData()
  }

  handleChangePageSize: ReactInputChangeEvent = ({ target: { value } }): void => {
    this.getQueryParams.pageIndex = 0
    this.getQueryParams.pageSize = parseInt(value)
    this.userPref.setRowsPerPage(value)
    this._getData()
  }

  handleSearch = (key: string, pageIndex = 0) => {
    this.getQueryParams.filter = key
    this.getQueryParams.pageIndex = pageIndex
    this._getData()
  }

  unselectItemById = (id: string) => {
    const index = this.props.elements.findIndex((element: any) => element.id === id)
    this.selectedIndexArr.delete(index)
  }

  selectItemById = (id: string) => {
    const index = this.props.elements.findIndex((element: any) => element.id === id)
    this.selectedIndexArr.add(index)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _filterElements = (filter?: string) => {
    return new Promise<Array<any>>((resolve) => {
      if (!filter) return resolve(this.props.elements)

      const regexp = new RegExp(`[^]*${filter}[^]*`, 'gi')
      resolve(this.props.elements.filter((element) => element[`${this.props.filterKey}`].match(regexp)))
    })
  }
  _sortElements = (elements: Array<any>, order?: string, orderBy = this.props.filterKey) => {
    return new Promise<Array<any>>((resolve) => {
      resolve(
        orderBy
          ? [...elements].sort((firstElem, secondElem) => {
              // @ts-ignore
              return this[`_${this.props.headerProps.find(({ key }) => key === orderBy)!.type}Sort`](
                order,
                firstElem[`${orderBy}`],
                secondElem[`${orderBy}`]
              )
            })
          : elements
      )
    })
  }

  _numberSort = (order: string, firstElem: string, secondElem: string) => {
    const firstNum = parseInt(firstElem)
    const secondNum = parseInt(secondElem)
    if (order === 'desc') return secondNum - firstNum
    else return firstNum - secondNum
  }

  _dateSort = (order: string, firstElem: string, secondElem: string) => {
    const firstDate = new Date(firstElem)
    const secondDate = new Date(secondElem)
    if (order === 'desc') return secondDate > firstDate ? 1 : -1
    else return firstDate > secondDate ? 1 : -1
  }

  _stringSort = (order: string, firstElem: string, secondElem: string) => {
    if (order === 'desc') return secondElem > firstElem ? 1 : -1
    else return firstElem > secondElem ? 1 : -1
  }

  _splitElements = (elements: Array<any>, size: number, pageIndex: number) => {
    return new Promise<Array<any>>((resolve) => {
      const start = size * pageIndex
      resolve(elements.slice(start, start + size))
    })
  }

  handleTableRowClick: ReactClickEvent = (event) => {
    if (this.props.onRowClick) this.props.onRowClick(event)
  }

  getData = () => {
    this._getData()
  }
}

export default TableContainer
